import { useEffect, useRef, useState } from "react";

const BRAND_GREEN = "#0A4730";
const BRAND_LIME = "#C0F6B7";
const SOFT_WHITE = "#F7FAF8";
const MUTED_GREEN = "#8FB5A6";

export function BluPaySplash({
  onDone,
  sendLabel = "USD",
  receiveLabel = "GHS",
}) {
  const [phase, setPhase] = useState("entering");
  const onDoneRef = useRef(onDone);

  useEffect(() => {
    onDoneRef.current = onDone;
  }, [onDone]);

  useEffect(() => {
    const holdTimer = window.setTimeout(() => {
      setPhase("holding");
    }, 900);

    const exitTimer = window.setTimeout(() => {
      setPhase("exiting");
    }, 2200);

    const doneTimer = window.setTimeout(() => {
      setPhase("done");
      onDoneRef.current?.();
    }, 2750);

    return () => {
      window.clearTimeout(holdTimer);
      window.clearTimeout(exitTimer);
      window.clearTimeout(doneTimer);
    };
  }, []);

  if (phase === "done") {
    return null;
  }

  return (
    <div
      role="status"
      aria-label="BluPay is loading"
      aria-live="polite"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 9999,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        overflow: "hidden",
        background: BRAND_GREEN,
        opacity: phase === "exiting" ? 0 : 1,
        visibility: phase === "exiting" ? "hidden" : "visible",
        transition:
          "opacity 520ms cubic-bezier(0.4, 0, 0.2, 1), visibility 520ms",
      }}
    >
      <style>{`
        @keyframes blupay-splash-glow {
          0% {
            opacity: 0;
            transform: translate(-50%, -50%) scale(0.72);
          }

          100% {
            opacity: 1;
            transform: translate(-50%, -50%) scale(1);
          }
        }

        @keyframes blupay-splash-path {
          from {
            stroke-dashoffset: 220;
          }

          to {
            stroke-dashoffset: 0;
          }
        }

        @keyframes blupay-splash-dot {
          0% {
            offset-distance: 0%;
            opacity: 0;
            transform: scale(0.7);
          }

          12% {
            opacity: 1;
            transform: scale(1);
          }

          88% {
            opacity: 1;
            transform: scale(1);
          }

          100% {
            offset-distance: 100%;
            opacity: 0;
            transform: scale(0.7);
          }
        }

        @keyframes blupay-splash-label {
          from {
            opacity: 0;
            transform: translateY(4px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes blupay-splash-mark {
          from {
            opacity: 0;
            transform: translateY(10px) scale(0.98);
          }

          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes blupay-splash-copy {
          from {
            opacity: 0;
            transform: translateY(6px);
          }

          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .blupay-splash-glow,
          .blupay-splash-path,
          .blupay-splash-dot,
          .blupay-splash-labels,
          .blupay-splash-wordmark,
          .blupay-splash-copy {
            animation: none !important;
            opacity: 1 !important;
            transform: none !important;
          }

          .blupay-splash-dot {
            display: none !important;
          }
        }
      `}</style>

      <div
        className="blupay-splash-glow"
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: "min(620px, 120vw)",
          height: "min(620px, 120vw)",
          borderRadius: "50%",
          background: `
            radial-gradient(
              circle,
              rgba(192, 246, 183, 0.11) 0%,
              rgba(192, 246, 183, 0.04) 42%,
              rgba(192, 246, 183, 0) 72%
            )
          `,
          animation:
            "blupay-splash-glow 1200ms cubic-bezier(0.16, 1, 0.3, 1) both",
          pointerEvents: "none",
        }}
      />

      <main
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 360,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: "relative",
            width: 238,
            height: 76,
            marginBottom: 25,
          }}
        >
          <svg
            width="238"
            height="76"
            viewBox="0 0 238 76"
            fill="none"
            style={{
              position: "absolute",
              inset: 0,
              overflow: "visible",
            }}
          >
            <path
              className="blupay-splash-path"
              d="M 22 58 Q 119 -10 216 58"
              stroke={BRAND_LIME}
              strokeOpacity="0.32"
              strokeWidth="1.5"
              strokeDasharray="4 6"
              strokeDashoffset="220"
              strokeLinecap="round"
              style={{
                animation:
                  "blupay-splash-path 1100ms cubic-bezier(0.16, 1, 0.3, 1) 120ms both",
              }}
            />
          </svg>

          <div
            className="blupay-splash-dot"
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: 10,
              height: 10,
              borderRadius: "50%",
              background: BRAND_LIME,
              boxShadow: `
                0 0 0 5px rgba(192, 246, 183, 0.1),
                0 0 22px rgba(192, 246, 183, 0.42)
              `,
              offsetPath: "path('M 22 58 Q 119 -10 216 58')",
              offsetRotate: "0deg",
              animation:
                "blupay-splash-dot 1500ms cubic-bezier(0.4, 0, 0.2, 1) 180ms both",
            }}
          />

          <div
            className="blupay-splash-labels"
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "space-between",
              padding: "0 4px",
              animation:
                "blupay-splash-label 500ms ease-out 100ms both",
            }}
          >
            <CurrencyPoint label={sendLabel} align="left" />
            <CurrencyPoint label={receiveLabel} align="right" />
          </div>
        </div>

        <div
          className="blupay-splash-wordmark"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            color: SOFT_WHITE,
            animation:
              "blupay-splash-mark 700ms cubic-bezier(0.16, 1, 0.3, 1) 620ms both",
          }}
        >
        <img
            src="/BlupayLogo.png"
            alt=""
            aria-hidden="true"
            style={{
                width: 48,
                height: 48,
                display: "block",
                objectFit: "contain",
                flexShrink: 0,
                filter: "drop-shadow(0 12px 22px rgba(0, 0, 0, 0.16))",
            }}
            />

          <span
            style={{
              fontFamily: "'Georgia', 'Times New Roman', serif",
              fontSize: 34,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "-0.025em",
            }}
          >
            BluPay
          </span>
        </div>

        <p
          className="blupay-splash-copy"
          style={{
            margin: "18px 0 0",
            color: MUTED_GREEN,
            fontFamily:
              "Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            lineHeight: 1.5,
            letterSpacing: "0.015em",
            animation:
              "blupay-splash-copy 600ms ease-out 900ms both",
          }}
        >
          Money moves better with BluPay.
        </p>
      </main>
    </div>
  );
}

function CurrencyPoint({ label, align }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: align === "left" ? "flex-start" : "flex-end",
        gap: 7,
      }}
    >
      <span
        style={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: BRAND_LIME,
          boxShadow: "0 0 0 4px rgba(192, 246, 183, 0.08)",
        }}
      />

      <span
        style={{
          color: MUTED_GREEN,
          fontFamily:
            "Inter, ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          letterSpacing: "0.14em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
    </div>
  );
}